import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Pencil, ScanLine, ShieldCheck, Sparkles, Trash2, UserCircle, Wrench } from "lucide-react";

import { api } from "@/lib/api";
import { labelFor } from "@/lib/options";
import { StyleRowCard } from "@/components/cards";
import { StyleCarousel } from "@/components/home";
import { Button, Card, WaveBackdrop } from "@/components/kit";
import { Page } from "@/components/layout";
import { useApp } from "@/store";

const CARD_FIELDS = [
  { key: "face_shape", label: "Face shape" },
  { key: "hair_pattern", label: "Hair pattern" },
  { key: "length", label: "Length" },
  { key: "density", label: "Density" },
  { key: "thickness", label: "Thickness", note: "You said" },
  { key: "preferred_style", label: "Style pref." },
  { key: "finish", label: "Finish" },
  { key: "hold", label: "Hold" },
];

export default function MyStyle() {
  const { profile, hasProfile, saved, mode, user, logout, toast, recs, topSlugs, clearScanHistory } = useApp();
  const [savedStyles, setSavedStyles] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!saved.length) return setSavedStyles([]);
    api(`/hairstyles?slugs=${saved.join(",")}`)
      .then((r) => setSavedStyles(saved.map((s) => r.find((x) => x.slug === s)).filter(Boolean)))
      .catch((e) => toast(e.message, "error"));
  }, [saved, toast]);

  const deleteScan = async () => {
    setDeleting(true);
    try {
      await clearScanHistory();
      toast("Scan history deleted.", "success");
      setConfirmDelete(false);
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setDeleting(false);
    }
  };
  const val = (key) => (key === "hair_pattern" && !profile.hair_pattern && profile.hair_type ? labelFor("hair_type", profile.hair_type) : labelFor(key, profile[key]));

  return (
    <div data-testid="mystyle-screen">
      <Page wide className="flex flex-col gap-1 border-b border-divider pb-4 pt-6">
        <p className="t-label">{mode === "user" ? user?.email : "Guest · saved in this browser"}</p>
        <h1 className="t-h1">MY STYLE.</h1>
      </Page>
      <Page wide className="grid grid-cols-1 gap-6 py-6 lg:grid-cols-[1.1fr_1fr] [&>*]:min-w-0">
        <div className="flex flex-col gap-6">
          <section data-testid="hair-profile-card" className="relative overflow-hidden rounded-[20px] bg-navy p-6">
            <WaveBackdrop tone="dark" />
            <div className="relative">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-body-bold text-[11px] tracking-[1.4px] text-sky">YOUR HAIR PROFILE</p>
                  <h2 className="mt-1 font-display text-[26px] text-white">{hasProfile ? `${val("hair_pattern")} · ${val("length")}` : "Not set yet"}</h2>
                </div>
                <button type="button" data-testid="hair-profile-edit-button" aria-label="Edit hair profile" onClick={() => navigate("/edit-profile")} className="focus-ring flex h-11 w-11 items-center justify-center rounded-full bg-sky text-navy transition-transform hover:scale-105">
                  <Pencil className="h-[18px] w-[18px]" />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-y-3 sm:grid-cols-4">
                {CARD_FIELDS.map((f) => {
                  const conf = profile.confidences?.[f.key];
                  return (
                    <div key={f.key} data-testid={`hair-profile-${f.key}`} className="min-w-0 pr-2">
                      <p className="font-body-bold text-[10px] tracking-[1px] text-soft">{f.label.toUpperCase()}</p>
                      <p className="mt-0.5 truncate font-display-semi text-[17px] text-white">{val(f.key)}</p>
                      <p className="font-body text-[10px] text-sky">{conf != null ? `AI est. · ${Math.round(conf * 100)}%` : f.note && profile[f.key] ? f.note : " "}</p>
                    </div>
                  );
                })}
              </div>
              {profile.strand_notes ? <p className="mt-3 font-body text-[13px] text-soft">{profile.strand_notes}</p> : null}
              <p className="mt-4 font-body text-[11px] italic text-soft">AI styling suggestions are estimates, not professional or medical assessments.</p>
            </div>
          </section>
          {hasProfile ? (
            <>
              {recs?.length ? <StyleCarousel title="Your top 5" styles={recs.map((r) => r.style)} testPrefix="mystyle-top" badges={false} action="See all" onAction={() => navigate("/recommendations")} /> : null}
              <Button testId="mystyle-see-recommendations" title="Styles made for you" icon={Sparkles} onClick={() => navigate("/recommendations")} />
            </>
          ) : (
            <div className="flex gap-3">
              <Button testId="mystyle-scan-button" title="Scan" icon={ScanLine} onClick={() => navigate("/scan")} className="flex-1" />
              <Button testId="mystyle-manual-button" title="Manual" variant="outline" onClick={() => navigate("/edit-profile?next=recs")} className="flex-1" />
            </div>
          )}
          <section>
            <h2 className="t-h2 mb-3">Saved hairstyles</h2>
            {savedStyles.length ? (
              <div className="flex flex-col gap-3">
                {savedStyles.map((s) => <StyleRowCard key={s.slug} testId={`saved-style-${s.slug}`} style={s} forYou={topSlugs.has(s.slug)} onClick={() => navigate(`/style/${s.slug}`)} />)}
              </div>
            ) : (
              <Card testId="saved-empty"><p className="t-strong">No saved styles yet.</p><p className="t-body">Tap Save on any hairstyle to keep it here.</p></Card>
            )}
          </section>
        </div>
        <Card className="h-fit lg:sticky lg:top-24">
          <p className="t-label">Account &amp; privacy</p>
          <p className="t-body mt-2">Scan photos are never stored — they&apos;re analysed in memory and discarded. Only the details you save appear in your profile.</p>
          <div data-testid="mystyle-delete-scan-section" className="mt-4 flex flex-col gap-2 rounded-xl bg-off p-3">
            <p className="t-strong">Scan history &amp; saved images</p>
            <p className="t-caption">Clears your saved scan results{mode === "user" ? " (in this browser and in your account)" : " in this browser"}, plus any scan selfie or try-on preview kept in the app. Previews you downloaded stay on your device — delete them there.</p>
            {confirmDelete ? (
              <div className="flex gap-3">
                <Button testId="mystyle-delete-scan-confirm" title="Delete" icon={Trash2} loading={deleting} onClick={deleteScan} className="flex-1" />
                <Button testId="mystyle-delete-scan-cancel" title="Cancel" variant="outline" onClick={() => setConfirmDelete(false)} className="flex-1" />
              </div>
            ) : <Button testId="mystyle-delete-scan" title="Delete scan history" icon={Trash2} variant="outline" onClick={() => setConfirmDelete(true)} />}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            {mode === "user" ? <Button testId="mystyle-account" title="Account" icon={UserCircle} variant="outline" onClick={() => navigate("/account")} /> : null}
            <Button testId="mystyle-privacy" title="Privacy centre" icon={ShieldCheck} variant="outline" onClick={() => navigate("/privacy")} />
          </div>
          {user?.role === "admin" ? <Button testId="mystyle-admin" title="Admin dashboard" icon={Wrench} onClick={() => navigate("/admin")} className="mt-4 w-full" /> : null}
          {mode === "user" ? (
            <Button testId="mystyle-logout" title="Sign out" variant="outline" icon={LogOut} onClick={async () => { await logout(); navigate("/", { replace: true }); }} className="mt-4 w-full" />
          ) : (
            <div className="mt-4 flex flex-col gap-2">
              <Button testId="mystyle-create-account" title="Create account to sync" onClick={() => navigate("/auth?mode=register")} />
              <Button testId="mystyle-sign-in" title="Sign in" variant="ghost" onClick={() => navigate("/auth?mode=login")} />
            </div>
          )}
        </Card>
      </Page>
    </div>
  );
}
